import type { UserPermissionValues } from "Infrastructure/Components/PermissionGuard";
import { themeAtom } from "Infrastructure/Theme/Atom/themeAtom";
import { Resources, useResource } from "Infrastructure/Translation/Resources";
import { hasPermission } from "Infrastructure/Utilities/Permissions/hasPermission";
import { NavItem } from "Shared/Components/Layout/Full/Sidebar/NavItem";
import type {
	MenuItemsType,
	MenuItemTypes,
} from "Shared/Components/Layout/Full/Sidebar/Types/MenuItem";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Collapse, ListItemIcon, ListItemText } from "@mui/material";
import { useAtom } from "jotai";
import type { ElementType } from "react";
import { Fragment, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { ListItem } from "./ListItemStyled";

export type NavGroupProps = {
	navlabel?: boolean;
	subheader?: string;
	title?: MenuItemTypes;
	icon?: ElementType;
	href?: string;
	id?: string;
	children?: NavGroupProps[];
	permission?: UserPermissionValues | UserPermissionValues[];
};

interface NavCollapseProps {
	menu?: NavGroupProps;
	level: number;
	pathWithoutLastPart: string;
	pathDirect: string;
	onClick: (event: React.MouseEvent<HTMLElement>) => void;
}

const PageResources = Resources.base.menu;

export const NavCollapse = ({
	menu,
	level,
	pathWithoutLastPart,
	pathDirect,
	onClick,
}: NavCollapseProps) => {
	const Icon = menu?.icon;
	const [themeConfiguration] = useAtom(themeAtom);
	const { pathname } = useLocation();
	const { t } = useResource();
	const [open, setOpen] = useState(false);
	const { isCollapse, isSidebarHover } = themeConfiguration;

	const menuIcon =
		level > 1
			? Icon && <Icon fontSize="medium" />
			: Icon && <Icon fontSize="small" />;

	const handleClick = () => {
		setOpen(!open);
	};

	const hasPerm = hasPermission(menu?.permission, true);

	useEffect(() => {
		menu?.children?.forEach((item: MenuItemsType) => {
			if (item?.href === pathname) {
				setOpen(true);
			}
		});
	}, [pathname, menu?.children]);

	useEffect(() => {
		if (!isSidebarHover) {
			setOpen(false);
		}
	}, [isSidebarHover]);

	// If Menu has Children
	const submenus = menu?.children?.map((item: MenuItemsType) => {
		if (item.children && hasPerm) {
			return (
				<NavCollapse
					key={`nav-collapse-${item?.id}`}
					menu={item}
					level={level + 1}
					pathWithoutLastPart={pathWithoutLastPart}
					pathDirect={pathDirect}
					onClick={onClick}
				/>
			);
		}

		return (
			<NavItem
				key={`nav-item-${item?.id}`}
				item={item}
				level={level + 1}
				pathDirect={pathDirect}
				onClick={onClick}
			/>
		);
	});

	return (
		hasPerm && (
			<Fragment key={`menu-${menu?.id}`}>
				<ListItem
					onClick={handleClick}
					selected={pathWithoutLastPart === menu?.href}
					key={menu?.id}
					open={open}
					level={level}
					menu={menu}
				>
					<>
						<ListItemIcon
							sx={{
								minWidth: "36px",
								color: "inherit",
								fontSize: level > 1 ? "1.3rem" : "1rem",
							}}
						>
							{menuIcon}
						</ListItemIcon>

						{(!isCollapse || isSidebarHover) && (
							<>
								<ListItemText color="inherit">
									{menu?.title && t(PageResources.navigation[menu.title])}
								</ListItemText>
								{!open ? (
									<ExpandMoreIcon fontSize="small" />
								) : (
									<ExpandLessIcon fontSize="small" />
								)}
							</>
						)}
					</>
				</ListItem>
				<Collapse in={open} timeout="auto" unmountOnExit>
					{submenus}
				</Collapse>
			</Fragment>
		)
	);
};
